// Constants for the Crude Procurement module (Plan & Procurement).
//
// Procurement is the business stage AFTER Planning and is fed by it: a Planning
// Request that reached "Request Submitted" becomes a Procurement record. From
// there it has its own Draft -> Submitted lifecycle, and submitting it advances
// the whole thing into the next stage (Tender Opening).
//
// Deliberately a SEPARATE constant set from Planning's `constants.js` (the user
// chose to keep the two modules independent). Values map 1:1 to backend enums
// once the contract arrives; everything runs on dummy data for now.

// Fixed procurement type — carried over from Planning, shown read-only.
export const PROCUREMENT_TYPE = 'Hydrocarbon Crude & Condensate';

// Procurement Status — the sub-status within the Procurement stage. Drives the
// list's Action column: Draft -> Edit, Submitted -> View Detail.
export const PROC_STATUS = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
};

// Main Status — the overall stage the record sits in. Procurement while being
// worked on; Submit advances it to Tender Opening (the next module).
export const MAIN_STATUS = {
  PROCUREMENT: 'Procurement',
  TENDER_OPENING: 'Tender Opening',
};

// Status Tender Opening — the next stage's sub-status. Not a Procurement list
// column; it is stamped 'Open' on Submit so the (future) Tender Opening module
// can pick the record up. Kept here so the Submit logic has a single source.
export const TENDER_OPENING_STATUS = {
  OPEN: 'Open',
};

// Procurement method (mekanisme pengadaan) — inherited from Planning.
export const PROCUREMENT_METHODS = [
  'Pelelangan Khusus',
  'Pemilihan Langsung',
  'Tunjuk Langsung',
];

// The four Overview / checklist steps. Planning has the first three; `validity`
// is specific to Procurement.
export const STEP = {
  REQUIREMENT: 'requirement',
  SUPPORTING_DOCUMENT: 'supportingDocument',
  DMUT: 'dmut',
  VALIDITY: 'validity',
};

export const STEP_STATUS = {
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
};

// Raw wire values for `PlanningStepStatus` (procurementApi maps STEP_STATUS <->
// these). Shared enum with Planning; the C# member names have no space, unlike
// the display labels above.
export const STEP_STATUS_API = {
  IN_PROGRESS: 'InProgress',
  COMPLETED: 'Completed',
};

// Raw wire values for `DocumentSource` (GetProcurementValidityResponse
// documents[].source / planningDocuments[].source).
export const DOC_SOURCE_API = {
  MASTER: 'MasterDocument',
  CUSTOM: 'UploadCustom',
};

// Checklist colour: orange while In Progress, green once Completed.
export const STEP_COLOR = {
  [STEP_STATUS.IN_PROGRESS]: 'warning',
  [STEP_STATUS.COMPLETED]: 'success',
};

// Order + labels of the four checklist columns in the list.
export const STEP_COLUMNS = [
  { key: STEP.REQUIREMENT, label: 'Procurement Requirement' },
  { key: STEP.SUPPORTING_DOCUMENT, label: 'Supporting Document' },
  { key: STEP.DMUT, label: 'DMUT List' },
  { key: STEP.VALIDITY, label: 'Validity' },
];

// Only a submitted procurement is read-only; a draft is still editable.
export const isEditable = status => status === PROC_STATUS.DRAFT;

// Badge colours for the two status columns.
export const PROC_STATUS_BADGE = {
  [PROC_STATUS.DRAFT]: 'warning',
  [PROC_STATUS.SUBMITTED]: 'success',
};

export const MAIN_STATUS_BADGE = {
  [MAIN_STATUS.PROCUREMENT]: 'primary',
  [MAIN_STATUS.TENDER_OPENING]: 'info',
};

// Requirement tab inner section navigation (view-only in Procurement).
// "Other Clause" dihapus mengikuti Planning: catatan bebas kini tampil di
// "Notes for Cargo Requirement" (cargoNotes) di dalam section Cargo.
export const REQUIREMENT_SECTIONS = [
  { id: 'contract', label: 'Contract' },
  { id: 'cargo', label: 'Cargo Specification' },
  { id: 'clause', label: 'Clause Detail' },
];

// Document source labels shown in the Supporting Document lists.
export const DOC_SOURCE = {
  MASTER: 'Master Document',
  CUSTOM: 'Upload Custom',
};
